import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';

import { SharedDataTags } from '../../helpers/shared-data/shared-data.enum';
import { IServiceResponseWidgets } from '../../models/services/response-app/response-app-widgets.interface';
import { IServiceResponseNavigationTree, IServiceResponseUsuario } from '../../models/services/response-app/response-app.interface';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class StoreService {
  private _profile: Subject<IServiceResponseUsuario> = new ReplaySubject();
  private _menu: Subject<IServiceResponseNavigationTree> = new ReplaySubject();
  private _widgetList: Subject<IServiceResponseWidgets[]> = new ReplaySubject();
  private _modalStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _modalIdReference: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private _modalTitle: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private _userToken: Subject<string> = new ReplaySubject();
  private _userTokenExpiration: Subject<number> = new ReplaySubject();
  private _userOperator: Subject<string> = new ReplaySubject();
  private _validSession: Subject<boolean> = new Subject();
  private _refScriptList = {};
  private _scriptList = new ReplaySubject();
  private _refSharedData: { [key: string]: ReplaySubject<any> } = {};

  constructor(private zone: NgZone, private router: Router) {
    // @ts-ignore
    window['setSharedData'] = (dataKey: string, data: any) => this.zone.run(() => this.setSharedData(dataKey, data));
    // @ts-ignore
    window['getSharedData'] = (dataKey: string) => this.zone.run(() => this.getSharedData(dataKey));
    // @ts-ignore
    window['scrollWindow'] = () => this.zone.run(() => this.setScrollToTop());
    // @ts-ignore
    window['redirect'] = (path: string) => this.zone.run(() => this.setRedirect(path));
  }

  /**
   * Creates reference inside refSharedData object to later set or get the information
   * @param dataKey key corresponding information stored
   */
  createSharedDataReference(dataKey: string): void {
    if (!this._refSharedData[dataKey]) {
      this._refSharedData[dataKey] = new ReplaySubject(1);
    }
  }

  /**
   * Returns information stored inside shell app
   * @param dataKey key corresponding information stored
   * @returns information stored
   */
  getSharedData(dataKey: string) {
    this.createSharedDataReference(dataKey);
    return this._refSharedData[dataKey].asObservable();
  }

  /**
   * Sets public information to be shared between shell and available to all microfrontend applications
   * @param dataKey key corresponding information stored
   * @param data information to be stored
   */
  setSharedData(dataKey: string, data: any): void {
    this.createSharedDataReference(dataKey);
    this._refSharedData[dataKey].next(data);
  }

  /**
   * Sets shared profile info to be available for use by microfrontends
   * @param profile profile recovered from /app API
   */
  setSharedProfileInfo(profile: IServiceResponseUsuario): void {
    this.setSharedData(SharedDataTags.INFO_PROFILE, {
      operador: profile.operador,
      empresas: profile.empresas
    });
  }

  get profile() {
    return this._profile.asObservable();
  }
  setProfile(profileInformation: IServiceResponseUsuario) {
    this.setSharedProfileInfo(profileInformation);
    this._profile.next(profileInformation);
  }

  get userOperator() {
    return this._userOperator.asObservable();
  }
  setUserOperator(userOperator: string) {
    this._userOperator.next(userOperator);
    this.setSharedData(SharedDataTags.USER_OPERATOR, userOperator);
  }

  get userToken() {
    return this._userToken.asObservable();
  }

  get userTokenValue() {
    return this._userToken;
  }
  setUserToken(userToken: string) {
    this._userToken.next(userToken);
    this.setSharedData(SharedDataTags.USER_TOKEN, userToken);
  }

  get tokenExpiration() {
    return this._userToken.asObservable();
  }
  setTokenExpiration(tokenExpiration: number) {
    this._userTokenExpiration.next(tokenExpiration);
    this.setSharedData(SharedDataTags.TOKEN_EXPIRATION, tokenExpiration);
  }

  get menu() {
    return this._menu.asObservable();
  }
  setMenu(menuInformation: IServiceResponseNavigationTree) {
    this._menu.next(menuInformation);
    this.setSharedData(SharedDataTags.MENU, menuInformation);
  }

  get widgetList() {
    return this._widgetList.asObservable();
  }
  setWidgetList(modalList: IServiceResponseWidgets[]) {
    this._widgetList.next(modalList);
  }

  get modalStatus() {
    return this._modalStatus.asObservable();
  }
  setModalStatus(value: boolean) {
    this._modalStatus.next(value);
  }

  get modalIdReference() {
    return this._modalIdReference.asObservable();
  }
  setModalIdReference(value: string) {
    this._modalIdReference.next(value);
  }

  get modalTitle() {
    return this._modalTitle.asObservable();
  }
  setModalTitle(modalTitle: string) {
    this._modalTitle.next(modalTitle);
  }

  get scriptList() {
    return this._scriptList.asObservable();
  }
  setScriptList(scriptItem: any) {
    this._refScriptList = { ...this._refScriptList, ...scriptItem };
    this._scriptList.next(this._refScriptList);
  }

  get validSession() {
    return this._validSession.asObservable();
  }
  setValidSession(sessionStatus: boolean) {
    this._validSession.next(sessionStatus);
  }

  setScrollToTop() {
    document.querySelector('body')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  setRedirect(path: string) {
    this.router.navigateByUrl(path);
  }
}
